import {
  BoxDownloadIcon,
  BoxExportIcon,
  CashIcon,
  ColumnIcons,
  DocumentIcon,
  DropOutIcon,
  GroupUsers,
  RefreshIcon,
} from "@assets/icons";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Skeleton } from "@components/ui/skeleton";
import DNDCOMP from "@pages/test";
import { endpoint } from "@services/api.services";
import FilterDropdown from "@ui/external/components/dropdown";
import { GeneralCard, TableCard } from "@ui/internal/components/card";
import FilterPopover from "@ui/internal/components/filter-popover";
import { exportToExcel, showToast } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { ColumnType, QueryMetaData } from "@utils/interface/index.interface";
import { StudentData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { useMetaStore, useSelectionStore } from "@utils/states/index.state";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ColumnList } from "./column/index.column";
import StudentForm, { GenerateCertificateForm } from "./form/index.form";
import CSVUploader from "@ui/internal/components/upload-csv/UploadCsv";
import { useMutation } from "@tanstack/react-query";
import { StudentImport } from "@services/Student";
import ErrorRowsModal from "@ui/internal/components/defective-row-extractor/DRX";

interface SelectionProps {
  total_students: number;
  total_unpaid_students: number;
  total_dropped_students: number;
  total_certificates: number;
}

/* Off by default, still re-enablable from the Column dropdown. "Batch Repeater"
   (is_batch_repeater) is the same student on the same batch, which the course_batch
   M2M can't even represent — it reads as duplicate leads, not course repetition, and
   sitting next to "Course Repeat" it invites misreading. */
const HIDDEN_COLUMNS = ["Batch Repeater"];

const index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { search, date } = useMetaStore();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [backendErrors, setBackendErrors] = useState<string[]>([""]);
  const pathname = usePathname();

  const { mutate: studentImport } = useMutation({
    mutationFn: async (formdata: FormData) => StudentImport(formdata),
    onSuccess: (backendErrors) => {
      // Invalidate queries or perform other actions on success
      console.log("Todo added successfully!");
      searchParams.delete("file");
      setSearchParams(searchParams);
      console.log("Backend errors:", backendErrors.data.errors);
      {
        backendErrors.data.errors.length > 0 && setShowErrorModal(true);
      }
      {
        backendErrors.data.errors.length > 0 &&
          setBackendErrors(backendErrors.data.errors);
      }
      showToast({
        title: "Student import",
        description: "Student Import done successfully!",
        type: "success",
      });
    },
    onError: (error: any) => {
      console.error("Error adding todo:", error.message);
      searchParams.delete("file");
      setSearchParams(searchParams);
      showToast({
        title: "Student import",
        description: error.message || "Something went wrong try again!",
        type: "error",
      });
    },
  });

  const { handleAction } = useHandleAction({});
  /* ================================================= api calls logic ================================================= */
  const { data, isPending, refetch, error } = useListData<
    QueryMetaData<StudentData, SelectionProps>
  >({
    params: {
      page: searchParams.get("tpage") || 1,
      size: searchParams.get("per") || 10,
      search,
      created_date__gte: date.from ? format(date.from, "yyyy-MM-dd") : "",
      created_date__lte: date.to ? format(date.to, "yyyy-MM-dd") : "",
      ordering: searchParams.get("ordering") || "order",
      /* FilterPopover writes this key from `endpointValue`, so it's hyphenated —
         same as the invoice and payment pages */
      course_batch__id: searchParams.get("course-batch") || "",
      payment_status: searchParams.get("payment_status") || "",
      status: searchParams.get("status") || "",
      is_show: searchParams.get("is_show") || "",
      is_batch_repeater: searchParams.get("is_batch_repeater") || "",
      is_repeat: searchParams.get("is_repeat") || "",
      is_masterclass:
        pathname == "masterclass-students"
          ? true
          : searchParams.get("is_masterclass") || "",
    },
    endpoint: endpoint["student"],
    key: `${pathname}-listing`,
    window: true,
  });
  // rollback
  useEffect(() => {
    if (error?.status === 404 && error?.data?.detail === "Invalid page.") {
      searchParams.set("tpage", "1");
      setSearchParams(searchParams);
    }
  }, [error]);

  const allColumns = ColumnList({ refetch });
  const { selectedRowDetails } = useSelectionStore();
  const [columns, setColumns] = useState<
    (ColumnType<StudentData> & { id: number; checked: boolean })[]
  >([]);

  useEffect(() => {
    const newColumns = allColumns.map((item, index) => ({
      ...item,
      id: index + 1,
      checked: !HIDDEN_COLUMNS.includes(String(item.name)),
    }));
    setColumns(newColumns);
  }, []);

  /* === import excel === */
  const [file, setFile] = useState<File | null>(null);
  const { mutate } = usePostData();
  const handleImport = () => {
    mutate(
      {
        endpoint: endpoint["import-excel-student"],
        data: { excel_file: file },
      },
      {
        onSuccess: ({ status }) => {
          if ([201].includes(status)) {
            refetch();
            showToast({
              description: `Student Created ${"Created"}`,
              type: "success",
              title: "Success",
            });
          }
        },
      }
    );
  };

  useEffect(() => {
    file && handleImport();
  }, [file]);

  return (
    <>
      <TableCard
        bulkActions
        table_content={{
          mode: "default",
          type: pathname,
        }}
        orientationPosition={true}
        tableHeaderData={
          <div className="flex flex-col w-full">
            <div className="grid-cols-1 grid lg:grid-cols-2 xl:grid-cols-4  w-full justify-between items-center gap-4">
              <div className="py-[23px] bg-white px-[32px] flex justify-start items-center gap-[24px] rounded-[8px] border border-[#ECECEC]">
                <span className="p-2 max-w-[250px] max-h-[250px] flex items-center justify-center rounded-full border border=2">
                  <GroupUsers />
                </span>
                <div className="">
                  <span className="text-[#969696] text-nowrap text-[14px]">
                    Total Students
                  </span>
                  <h5 className="font-bold text-[#2C2C2C] text-[36px] leading-[50px]">
                    {isPending ? (
                      <Skeleton className="h-[50px] w-[90px]" />
                    ) : (
                      data?.data?.summary?.total_students || 0
                    )}
                  </h5>
                </div>
              </div>
              <div className="py-[23px] bg-white px-[32px] flex justify-start items-center gap-[24px] rounded-[8px] border border-[#ECECEC]">
                <span className="p-2 max-w-[250px] max-h-[250px] flex items-center justify-center rounded-full border border=2">
                  <CashIcon />
                </span>
                <div className="">
                  <span className="text-[#969696] text-nowrap text-[14px]">
                    Total Unpaid
                  </span>
                  <h5 className="font-bold text-[#2C2C2C] text-[36px] leading-[50px]">
                    {isPending ? (
                      <Skeleton className="h-[50px] w-[90px]" />
                    ) : (
                      data?.data?.summary?.total_unpaid_students || 0
                    )}
                  </h5>
                </div>
              </div>
              <div className="py-[23px] bg-white px-[32px] flex justify-start items-center gap-[24px] rounded-[8px] border border-[#ECECEC]">
                <span className="p-2 max-w-[250px] max-h-[250px] flex items-center justify-center rounded-full border border=2">
                  <DropOutIcon />
                </span>
                <div className="">
                  <span className="text-[#969696] text-nowrap text-[14px]">
                    Total Dropouts
                  </span>
                  <h5 className="font-bold text-[#2C2C2C] text-[36px] leading-[50px]">
                    {isPending ? (
                      <Skeleton className="h-[50px] w-[90px]" />
                    ) : (
                      data?.data?.summary?.total_dropped_students || 0
                    )}
                  </h5>
                </div>
              </div>
              <div className="py-[23px] bg-white px-[32px] flex justify-start items-center gap-[24px] rounded-[8px] border border-[#ECECEC]">
                <span className="p-2   max-w-[250px] max-h-[250px]  rounded-full flex items-center justify-center  border border=2">
                  <DocumentIcon />
                </span>
                <div className="">
                  <span className="text-[#969696] text-nowrap text-[14px]">
                    Certificates Issued
                  </span>
                  <h5 className="font-bold text-[#2C2C2C] text-[36px] leading-[50px]">
                    {isPending ? (
                      <Skeleton className="h-[50px] w-[90px]" />
                    ) : (
                      data?.data?.summary?.total_certificates || 0
                    )}
                  </h5>
                </div>
              </div>
            </div>
            <h2 className="capitalize w-full my-5 flex justify-between items-center">
              Showing all datas for students
              <span className="flex gap-3 items-center">
                <FilterDropdown
                  className={"max-w-[250px]"}
                  title={
                    <>
                      <ColumnIcons /> Column
                    </>
                  }
                >
                  <div>
                    <DNDCOMP
                      items={columns.filter((item) => item.name !== "ID")}
                      setItems={setColumns}
                    />
                  </div>
                </FilterDropdown>
                <Button variant={"outline"}>
                  <RefreshIcon />
                </Button>
              </span>
            </h2>
          </div>
        }
        inject_ui_table_actions={
          <>
            <FilterPopover
              filters={[
                pathname == "masterclass-students"
                  ? null
                  : {
                      label: "Masterclass",
                      searchKey: "is_masterclass",
                      radio: true,
                      defaultOptions: [
                        {
                          label: "Masterclass",
                          value: "true",
                        },
                        {
                          label: "Regular",
                          value: "false",
                        },
                      ],
                    },
                {
                  label: "Course & Batch",
                  endpointValue: "course-batch",
                  queries: {
                    is_masterclass:
                      pathname == "masterclass-students" ? "true" : "false",
                  },
                  format: { key: "name", value: "id" },
                },
                {
                  label: "Course Status",
                  radio: true,
                  searchKey: "is_show",
                  defaultOptions: [
                    {
                      label: "Running",
                      value: "true",
                    },
                    {
                      label: "Completed",
                      value: "false",
                    },
                    {
                      label: "Dropout",
                      value: "dropout",
                    },
                  ],
                },
                {
                  label: "Payment Status",
                  searchKey: "payment_status",
                  radio: true,
                  defaultOptions: [
                    {
                      label: "Paid",
                      value: "paid",
                    },
                    {
                      label: "Unpaid",
                      value: "unpaid",
                    },
                  ],
                },
                {
                  label: "Dropout",
                  searchKey: "status",
                  radio: true,
                  defaultOptions: [
                    {
                      label: "Dropped Out",
                      value: "true",
                    },
                    {
                      label: "Not Dropped Out",
                      value: "false",
                    },
                  ],
                },
                {
                  label: "Batch Repeater",
                  searchKey: "is_batch_repeater",
                  radio: true,
                  defaultOptions: [
                    {
                      label: "Batch Repeater",
                      value: "true",
                    },
                    {
                      label: "Not Repeating",
                      value: "false",
                    },
                  ],
                },
                {
                  /* same course, different batch — separate flag from the above */
                  label: "Course Repeat",
                  searchKey: "is_repeat",
                  radio: true,
                  defaultOptions: [
                    {
                      label: "Repeat (New Batch)",
                      value: "true",
                    },
                    {
                      label: "First Time",
                      value: "false",
                    },
                  ],
                },
              ].filter(
                (f): f is Exclude<typeof f, null> => f !== null
              )}
              title="Filter"
              triggerText="Filter"
            />
            <Button
              startIcon={<BoxDownloadIcon />}
              type="button"
              className="gap-0"
              onClick={() => {
                searchParams.set("file", "true");
                setSearchParams(searchParams);
              }}
              // onClick={() => {
              //   document.getElementById("import-xlxs-file")?.click();
              // }}
              variant={"outline"}
            >
              Import .xlsx
            </Button>
            <input
              type="file"
              id="import-xlxs-file"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                e.target.files && setFile(e.target.files[0])
              }
              className="hidden"
            />
            {data && data?.data?.results?.length > 0 && (
              <Button
                startIcon={<BoxExportIcon />}
                type="button"
                className="gap-0"
                onClick={() =>
                  exportToExcel(
                    selectedRowDetails?.length > 0
                      ? selectedRowDetails
                      : data?.data?.results ?? [],
                    `students.xlsx`
                  )
                }
                variant={"outline"}
              >
                Export .xlsx
              </Button>
            )}
          </>
        }
        grid={
          <GeneralCard
            isChecked={false}
            type={"mentor"}
            onDelete={() => {}}
            onEdit={() => {}}
          />
        }
        form={<StudentForm />}
        formId={pathname}
        columns={columns}
        refetch={refetch}
        endpoint={endpoint["student"]}
        isPending={isPending}
        rows={data?.data?.results ?? []}
        count={data?.data?.count ?? 10}
      />

      {searchParams.get("mode") && (
        <Dialog
          open={searchParams.get("mode") ? true : false}
          onOpenChange={() => {
            handleAction({ type: "mode-close" });
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate Certificate Number</DialogTitle>
              <DialogDescription>
                <GenerateCertificateForm refetch={refetch} />
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
      {searchParams.get("file") && (
        <Dialog
          open={searchParams.get("file") ? true : false}
          onOpenChange={() => {
            searchParams.delete("file");
            setSearchParams(searchParams);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Upload File
                <a
                  className="text-green-800 underline ml-4"
                  href="https://docs.google.com/spreadsheets/d/1hdaSwYU3NsJplitN0l0qQsDqiepUbrRZi0g2MG8gQ3o/edit?gid=0#gid=0"
                  target="_blank"
                >
                  Use this Format
                </a>
              </DialogTitle>

              <DialogDescription>
                <CSVUploader
                  onUpload={studentImport}
                  setFile={setFile}
                  file={file}
                />
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
      <ErrorRowsModal
              isOpen={showErrorModal}
              onClose={() => setShowErrorModal(false)}
              errors={backendErrors}
              originalFile={file}
            />
    </>
  );
};

export default index;
